import { resolveStoredFileUrl } from "utils/storedFileUrl";
import {
	Card,
	CircularProgress,
	ClickAwayListener,
	Collapse,
	Divider,
	Fade,
	Grid,
	Icon,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	Skeleton,
	Tab,
	Tabs,
	Tooltip,
} from "@mui/material";

import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import menuIcon from "assets/images/icons/menu.png";
import noMenuIcon from "assets/images/icons/no-menu.png";
import searchPerson from "assets/images/icons/search-person.png";
import sinTacc from "assets/images/icons/sin-tacc.png";
import vegetariano from "assets/images/icons/vegetariano.png";
import SinDatos from "components/Carteles/SinDatos";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import gql from "graphql-tag";
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useLayoutEffect, useState } from "react";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import { UserContext } from "context/user";
import { SEARCH_UNIFIED_USERS } from "services/unifiedUsers";
import logo_alt from "assets/images/comedor_alt.png";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "90%",
	width: { xs: "90%", sm: "60%", xxl: "50%" },
	overflowY: "auto",
	p: 3,
};

export default function ModalNuevoPedido({ open, handleClose }) {
	const [dataSave, setDataSave] = useState({});
	const [tipoCliente, setTipoCliente] = useState("UPrO");
	const [search, setSearch] = useState("");
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [usuario, setUsuario] = useState(null);
	const [usuarios, setUsuarios] = useState([]);
	const [filtroSistema, setFiltroSistema] = useState("Todos");
	const [inputValue, setInputValue] = useState("");
	const [selectedProducto, setSelectedProducto] = useState(null);
	const [isWriting, setIsWriting] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [debouncing, setDebouncing] = useState(false);
	const { handleSnackbar } = useContext(MessageManager);
	const { user } = useContext(UserContext);
	const [idSede, setIdSede] = useState(-1);
	const [selectedLocation, setSelectedLocation] = useState(null);

	const { data: dataSedes } = useQuery(
		gql`
		  query sedes {
			sedes {
			  id
			  nombre
			}
		  }
		`,
		{
			skip: !open,
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const { data: datosCuenta } = useQuery(
		gql`
      query sedes($filter: JSON) {
        sedes(filter: $filter) {
          id
          nombre
          cuit
          direccion
          ptoVenta
          telefono
          idsFormasPago
          cbu
          alias
          nombreFantasia
          incioActividades
          ingresosBrutos
          email
        }
      }
    `, {
		variables: {
			filter: {
				id: user?.sede?.id
			}
		},
		skip: !user?.sede?.id,
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache"
	}
	);

	const [getMenusDelDia, { data: menusDelDia }] = useLazyQuery(
		gql`
      query getMenusDelDia($filter: JSON) {
        menusDelDia(filter: $filter, ignorarTurnos: true) {
          id
          precio
          fechaMenu
          limiteReservas
          cantidadReservas
          producto {
            id
            nombre
            disponibilidad
            tipoProducto {
              id
              tipo
            }
            categoriaProducto {
              id
              categoria
            }
            files {
              file
            }
          }
        }
      }
    `,
		{
			fetchPolicy: "no-cache",
			cachePolicy: "no-cache",
		}
	);

	const [venderMenuUniversitario, { loading: loadingSaveVenta }] = useMutation(
		gql`
      mutation venderMenuUniversitario($idMedioDePago: ID, $idProducto: ID, $idUsuario: ID, $appUsuario: String, $nombre: String, $apellido: String, $nro_doc: String, $tipoUsuario: String, $idSede: ID!, $destino: String!, $total: Float){
        venderMenuUniversitario(idMedioDePago: $idMedioDePago, idProducto: $idProducto, idUsuario: $idUsuario, appUsuario: $appUsuario, nombre: $nombre, apellido: $apellido, nro_doc: $nro_doc, tipoUsuario: $tipoUsuario, idSede: $idSede, destino: $destino, total: $total) {
          id
          url
        }
      }
    `
	);


	const [searchUnifiedUsers] = useLazyQuery(SEARCH_UNIFIED_USERS, {
		fetchPolicy: "network-only",
	});

	const [getValorMenu, { data: valorMenu, loading: loadingValorMenu }] = useLazyQuery(
		gql`
      query getValorMenu($id: ID, $idSede: Int, $appUsuario: String, $tipoUsuario: String) {
        valorMenu(id: $id, idSede: $idSede, appUsuario: $appUsuario, tipoUsuario: $tipoUsuario)
      }
    `, {
		fetchPolicy: "no-cache",
		cachePolicy: "no-cache",
	}
	);

	const debounce = (func, wait) => {
		let timeout;
		return (...args) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => func(...args), wait);
		};
	};

	const normalizarTexto = (value) => String(value || "").trim().toLowerCase();

	// Función unificada de búsqueda en todas las fuentes
	const handleSearchUnified = useCallback(
		debounce(async (searchTerm) => {
			if (!searchTerm || searchTerm.length < 2) {
				setUsuarios([]);
				setDebouncing(false);
				return;
			}

			try {
				setDebouncing(true);

				const { data } = await searchUnifiedUsers({
					variables: { searchTerm },
				});
				const allUsers = data?.searchUnifiedUsers || [];

				// Remover solo duplicados exactos del mismo sistema/usuario.
				const uniqueUsers = allUsers.filter((user, index, self) => {
					const currentKey = [
						normalizarTexto(user?.appUsuario),
						normalizarTexto(user?.id),
						normalizarTexto(user?.tipoUser || user?.tipoUsuario),
						normalizarTexto(user?.email),
						normalizarTexto(user?.username),
						normalizarTexto(user?.dni),
					].join("|");

					return (
						index ===
						self.findIndex((u) => {
							const compareKey = [
								normalizarTexto(u?.appUsuario),
								normalizarTexto(u?.id),
								normalizarTexto(u?.tipoUser || u?.tipoUsuario),
								normalizarTexto(u?.email),
								normalizarTexto(u?.username),
								normalizarTexto(u?.dni),
							].join("|");
							return currentKey === compareKey;
						})
					);
				});

				setUsuarios(uniqueUsers);
			} catch (error) {
				console.error("Error en búsqueda unificada:", error);
				setUsuarios([]);
			} finally {
				setDebouncing(false);
			}
		}, 1000),
		[searchUnifiedUsers]
	);

	const handleSearch = (event) => {
		setIsWriting(true);
		setInputValue(event.target.value);
		setDropdownOpen(true);
		const newTimeout = setTimeout(() => {
			setSearch(event.target.value);
			setIsWriting(false);
		}, 500);
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		setSearchTimeout(newTimeout);
	};

	useLayoutEffect(() => {
		if (open && datosCuenta?.sedes?.length > 0) {
			setShowModal(true);
		} else if (!open) {
			setShowModal(false);
		}
	}, [open, datosCuenta]);

	useEffect(() => {
		if (open) {
			setTipoCliente("UPrO");
			setUsuario(null);
			setUsuarios([]);
			setFiltroSistema("Todos");
			setSearch("");
			setInputValue("");
			setDropdownOpen(false);
			setSelectedProducto(null);
			setIdSede(-1);
			setSelectedLocation(null);
			getMenusDelDia();
			setDataSave({});
		}
	}, [open]);

	useEffect(() => {
		if (search && search.length >= 2) {
			handleSearchUnified(search);
		} else {
			setUsuarios([]);
		}
	}, [search, open, handleSearchUnified]);

	useEffect(() => {
		if (tipoCliente === "UPrO" && usuario && idSede !== -1) {
			getValorMenu({
				variables: {
					id: Number(usuario?.id) || null,
					idSede: Number(idSede) || null,
					appUsuario: usuario?.appUsuario || null,
					tipoUsuario: usuario?.tipoUser || null,
				},
			})
		} else if (tipoCliente === "Particular" && idSede !== -1) {
			// Para particulares usamos la sede seleccionada, no la del usuario
			getValorMenu({
				variables: {
					id: null,
					idSede: Number(idSede) || null,
				},
			})
		}
		setSelectedProducto(null);
	}, [usuario, tipoCliente, idSede, user]);

	const usuariosFiltrados =
		filtroSistema === "Todos"
			? usuarios
			: usuarios.filter((item) => item?.appUsuario === filtroSistema);

	return (
		<Modal
			aria-labelledby="transition-modal-title"
			aria-describedby="transition-modal-description"
			open={showModal}
			onClose={() => {
				if (!loadingSaveVenta) {
					handleClose();
				}
			}}
			closeAfterTransition
		>
			<Fade in={showModal}>
				<Card sx={style}>
					<SoftBox>
						<SoftBox display="flex" justifyContent="space-between">
							<SoftBox>
								<SoftTypography variant="h6">Nuevo pedido</SoftTypography>
							</SoftBox>
							<SoftBox display="flex" justifyContent="space-between">
								<SoftBox>
									<Tooltip title="Cerrar" placement="top">
										<Icon
											fontSize="medium"
											onClick={() => {
												if (!loadingSaveVenta) {
													handleClose();
												}
											}}
											sx={{ cursor: "pointer" }}
										>
											close
										</Icon>
									</Tooltip>
								</SoftBox>
							</SoftBox>
						</SoftBox>
						<SoftBox pt={2}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Card sx={{ height: "100%" }}>
										<SoftBox display="flex" height="100%">
											<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
												<SoftBox display="flex" alignItems="center" gap={1}>
													<SoftBox>
														<Card>
															<SoftBox
																bgColor={getUproColor("uproPink")}
																sx={{
																	width: "30px",
																	height: "30px",
																}}
																display="flex"
																justifyContent="center"
																alignItems="center"
															>
																<Icon fontSize="small" sx={{ color: "#fff" }}>
																	visibility
																</Icon>
															</SoftBox>
														</Card>
													</SoftBox>
													<SoftTypography variant="h6" fontWeight="bold">
														Verifique el medio de pago
													</SoftTypography>
												</SoftBox>
												<SoftTypography variant="h7" fontWeight="regular">
													Asegurese de que el cliente realice la transferencia antes de finalizar el
													pedido y compruebe el monto y la fecha.
												</SoftTypography>
											</SoftBox>
										</SoftBox>
									</Card>
								</Grid>
								<Grid item xs={12} sm={6}>
									{idSede !== 2 && (<Card sx={{ height: "100%" }}>
										<SoftBox display="flex" height="100%">
											<SoftBox p={2} display="flex" flexDirection="column" gap={1}>
												<SoftBox display="flex" alignItems="center" gap={1}>
													<SoftBox>
														<Card>
															<SoftBox
																bgColor={getUproColor("uproBlue")}
																sx={{
																	width: "30px",
																	height: "30px",
																}}
																display="flex"
																justifyContent="center"
																alignItems="center"
															>
																<Icon fontSize="small" sx={{ color: "#fff" }}>
																	account_balance
																</Icon>
															</SoftBox>
														</Card>
													</SoftBox>
													<SoftTypography variant="h6" fontWeight="bold">
														Datos de la cuenta
													</SoftTypography>
												</SoftBox>
												{datosCuenta?.sedes[0]?.alias && (
													<SoftBox display="flex" gap={1}>
														<SoftTypography variant="h7" fontWeight="regular">
															Alias:
														</SoftTypography>
														<SoftTypography variant="h7" fontWeight="bold">
															{datosCuenta?.sedes[0]?.alias}
														</SoftTypography>
													</SoftBox>
												)}
												{datosCuenta?.sedes[0]?.nombreFantasia && (
													<SoftBox display="flex" gap={1}>
														<SoftTypography variant="h7" fontWeight="regular">
															Titular:
														</SoftTypography>
														<SoftTypography variant="h7" fontWeight="bold">
															{datosCuenta?.sedes[0]?.nombreFantasia}
														</SoftTypography>
													</SoftBox>
												)}
												{datosCuenta?.sedes[0]?.cbu && (
													<SoftBox display="flex" gap={1}>
														<SoftTypography variant="h7" fontWeight="regular">
															CBU:
														</SoftTypography>
														<SoftTypography variant="h7" fontWeight="bold">
															{datosCuenta?.sedes[0]?.cbu}
														</SoftTypography>
													</SoftBox>
												)}
											</SoftBox>
										</SoftBox>
									</Card>)}
								</Grid>
							</Grid>
							<Divider />
							<SoftBox display="flex" flexDirection="column" gap={2}>
								<SoftBox
									sx={{
										width: "100%",
									}}
								>
									<InputLabel>
										<SoftTypography variant="caption" fontWeight="bold">
											Tipo de cliente
										</SoftTypography>
									</InputLabel>
										<Tabs
											value={tipoCliente}
										name="tipoCliente"
										onChange={(e, value) => {
												setTipoCliente(value);
												setUsuario(null);
												setUsuarios([]);
												setFiltroSistema("Todos");
												setSearch("");
												setInputValue("");
											setDropdownOpen(false);
											setSelectedProducto(null);
											setIdSede(-1);
											setSelectedLocation(null);
											setDataSave({ ...dataSave, usuario: null });
										}}
									>
										<Tab
											label={
												<SoftTypography
													variant="h6"
													fontWeight={tipoCliente === "UPrO" ? "bold" : "regular"}
												>
													UPrO
												</SoftTypography>
											}
											value={"UPrO"}
										/>
										<Tab
											label={
												<SoftTypography
													variant="h6"
													fontWeight={tipoCliente === "Particular" ? "bold" : "regular"}
												>
													Particular
												</SoftTypography>
											}
											value={"Particular"}
										/>
									</Tabs>
								</SoftBox>
								<Fade in={tipoCliente !== "Particular"} unmountOnExit>
									<SoftBox sx={{ position: "relative" }}>
										<InputLabel>
											<SoftTypography variant="caption" fontWeight="bold">
												Usuario
											</SoftTypography>
										</InputLabel>
										<ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
											<SoftBox>
												<SoftBox
													sx={{
														position: "relative",
														zIndex: 2,
														backgroundColor: "#fff",
														borderTopLeftRadius: 4,
														borderTopRightRadius: 4,
													}}
												>
													{usuario ? (
														<SoftInput
															placeholder="Escribe aquí el nombre o DNI..."
															icon={{
																component: (
																	<Icon color="primary">
																		{usuario.appUsuario === "Comedor"
																			? "restaurant"
																			: usuario.appUsuario === "Compras"
																				? "work"
																				: "school"}
																	</Icon>
																),
																direction: "left",
															}}
															value={makeNiceText(
																`${usuario?.nombre} ${usuario?.apellido}`,
																"nombre"
															)}
															onChange={(e) => {
																setUsuario(null);
																setIdSede(-1);
																setSelectedLocation(null);
																handleSearch(e);
															}}
															icon2={{
																component: (
																	<Tooltip title="Limpiar" placement="top">
																		<Icon
																			color="primary"
																			onClick={() => {
																				setUsuario(null);
																				setUsuarios([]);
																				setFiltroSistema("Todos");
																				setSearch("");
																				setDropdownOpen(false);
																				setInputValue("");
																				setIdSede(-1);
																				setSelectedLocation(null);
																			}}
																			sx={{
																				cursor: "pointer",
																			}}
																		>
																			close
																		</Icon>
																	</Tooltip>
																),
																direction: "right",
															}}
														/>
													) : (
														<SoftInput
															placeholder="Escribe aquí el nombre o DNI..."
															icon={{ component: "person_search", direction: "left" }}
															onChange={handleSearch}
															value={inputValue}
															icon2={
																isWriting && {
																	component: (
																		<SoftBox
																			display="flex"
																			alignItems="center"
																			sx={{
																				width: "100%",
																				height: "100%",
																			}}
																		>
																			<CircularProgress size={15} color="primary" />
																		</SoftBox>
																	),
																	direction: "right",
																}
															}
														/>
													)}
												</SoftBox>
												<Collapse
													in={dropdownOpen && Boolean(search) && usuarios?.length > 0}
													mountOnEnter
													unmountOnExit
												>
													<Card
														sx={{
															position: "absolute",
															top: "100%",
															left: 0,
															width: "100%",
															boxSizing: "border-box",
															backgroundColor: "#fff",
															borderTopLeftRadius: "0px !important",
															borderTopRightRadius: "0px !important",
															border: "1px solid rgba(0, 0, 0, 0.12)",
															paddingTop: "5px",
															marginTop: "-5px",
															borderTop: "0px",
															maxHeight: 200,
															overflowY: "auto",
															zIndex: 1,
														}}
													>
														<SoftBox display="flex" flexDirection="column" p={1}>
															<SoftBox display="flex" pb={1}>
																<Select
																	size="small"
																	fullWidth
																	value={filtroSistema}
																	onChange={(e) => setFiltroSistema(e.target.value)}
																>
																	<MenuItem value="Todos">Todos los sistemas</MenuItem>
																	<MenuItem value="Compras">Compras</MenuItem>
																	<MenuItem value="Comedor">Comedor</MenuItem>
																	<MenuItem value="Alumnos">Alumnos</MenuItem>
																</Select>
															</SoftBox>
															{debouncing ? (
																<SoftBox
																	display="flex"
																	flexDirection="column"
																	justifyContent="center"
																	alignItems="center"
																	sx={{
																		height: 150,
																		width: "100%",
																	}}
																	p={2}
																>
																	<Skeleton variant="text" width="100%" />
																	<Skeleton variant="text" width="100%" />
																	<Skeleton variant="text" width="100%" />
																	<Skeleton variant="text" width="100%" />
																</SoftBox>
															) : usuariosFiltrados?.length > 0 ? (
																usuariosFiltrados?.map((usuario, index) => (
																	<SoftBox
																		display="flex"
																		p={1}
																		gap={1}
																		key={`${usuario.id}-${index}`}
																		sx={{
																			cursor: "pointer",
																			"&:hover": {
																				backgroundColor: "#f0f0f0",
																			},
																		}}
																		onClick={() => {
																			setUsuario(usuario);
																			setDropdownOpen(false);
																		}}
																	>
																		<Icon color="primary">
																			{usuario.appUsuario === "Comedor"
																				? "restaurant"
																				: usuario.appUsuario === "Compras"
																					? "work"
																					: "school"}
																		</Icon>
																		<SoftTypography variant="h7" fontWeight="bold">
																			{makeNiceText(
																				`${usuario.nombre} ${usuario.apellido}`,
																				"nombre"
																			)}
																		</SoftTypography>
																		{usuario.dni && (
																			<SoftTypography variant="h7" fontWeight="regular">
																				{formatMoneyPunto(usuario.dni)}
																			</SoftTypography>
																		)}
																		<SoftBadge
																			badgeContent={usuario.appUsuario}
																			color={
																				usuario.appUsuario === "Comedor"
																					? "success"
																					: usuario.appUsuario === "Compras"
																						? "info"
																						: "warning"
																			}
																			size="xs"
																		/>
																	</SoftBox>
																))
															) : (
																<SinDatos />
															)}
														</SoftBox>
													</Card>
												</Collapse>
											</SoftBox>
										</ClickAwayListener>
									</SoftBox>
								</Fade>

								<SoftBox
									display="flex"
									flexDirection="column"
									gap={2}
								>
									<SoftBox
										display="flex"
										justifyContent="flex-start"
										alignItems="flex-start"
										flexDirection="column"
										width="100%"
									>
										<InputLabel>
											<SoftTypography variant="caption" fontWeight="bold">
												Sede
											</SoftTypography>
										</InputLabel>
										<Select
											fullWidth
											value={idSede || -1}
											onChange={(e) => {
												const newSedeId = Number(e.target.value);
												setIdSede(newSedeId);
												if (newSedeId !== 2) {
													setSelectedLocation("VM");
												} else {
													setSelectedLocation(null);
												}
											}}
											displayEmpty
											inputProps={{ "aria-label": "Sede" }}
										>
											<MenuItem value={-1} disabled>
												Seleccione una sede
											</MenuItem>
											{dataSedes?.sedes?.map((sede) => (
												<MenuItem key={sede.id} value={sede.id}>
													{sede.nombre}
												</MenuItem>
											))}
										</Select>
									</SoftBox>
									{idSede === 2 && (
										<SoftBox
											display="flex"
											justifyContent="flex-start"
											alignItems="flex-start"
											flexDirection="column"
											width="100%"
										>
											<InputLabel>
												<SoftTypography variant="caption" fontWeight="bold">
													Localidad
												</SoftTypography>
											</InputLabel>
											<Select
												fullWidth
												value={selectedLocation}
												onChange={(e) => {
													setSelectedLocation(e.target.value);
												}}
												displayEmpty
											>
												<MenuItem value="" disabled>
													Seleccione una localidad
												</MenuItem>
												<MenuItem value="Tercera Rotonda">Tercera Rotonda</MenuItem>
												<MenuItem value="Centenario">Centenario</MenuItem>
												<MenuItem value="Serranías Puntanas">Serranías Puntanas</MenuItem>
											</Select>
										</SoftBox>
									)}
								</SoftBox>
								<Fade in={loadingValorMenu} unmountOnExit>
									<SoftBox
										display="flex"
										justifyContent="center"
										alignItems="center"
										height="100%"
										minHeight="50vh"
										sx={{
											position: "relative",
											zIndex: 10,
											backgroundColor: "rgba(255, 255, 255, 0.9)",
											borderRadius: 2
										}}
									>
										<SoftBox
											display="flex"
											justifyContent="center"
											alignItems="center"
											flexDirection="column"
											p={3}
										>
											<SoftBox component="img" src={logo_alt} alt="UPrO Logo" width="15rem" p={3} />
											<SoftTypography variant="h4" align="center" color="dark">
												Cargando datos
											</SoftTypography>
											<SoftBox
												display="flex"
												justifyContent="center"
												alignItems="center"
												flexDirection="column"
												gap={2}
											>
												<SoftTypography variant="h5" align="center" color="text">
													Por favor espera
												</SoftTypography>
												<CircularProgress size={30} color="primary" />
											</SoftBox>
										</SoftBox>
									</SoftBox>
								</Fade>
								<Fade in={
									(
										(tipoCliente === "UPrO" && (!usuario?.id || (idSede === 2 ? selectedLocation === null : idSede === -1))) ||
										(tipoCliente === "Particular" && (idSede === 2 ? selectedLocation === null : idSede === -1))
									) &&
									!loadingValorMenu
								} unmountOnExit>
									<SoftBox
										display="flex"
										justifyContent="center"
										alignItems="center"
										height="100%"
										minHeight="50vh"
									>
										<SoftBox
											display="flex"
											justifyContent="center"
											alignItems="center"
											flexDirection="column"
										>
											<SoftBox
												component="img"
												src={searchPerson}
												alt="UPrO Logo"
												width="10rem"
												p={3}
												sx={{
													animation: "shakes 1s",
													"@keyframes shakes": {
														"0%": {
															transform: "rotate(0deg)",
														},
														"25%": {
															transform: "rotate(10deg)",
														},
														"50%": {
															transform: "rotate(-20deg)",
														},
														"75%": {
															transform: "rotate(15deg)",
														},
														"100%": {
															transform: "rotate(-5deg)",
														},
													},
												}}
											/>
											<SoftTypography variant="h6" align="center">
												{tipoCliente === "UPrO"
													? "Primero seleccione un usuario y una sede"
													: "Primero seleccione una sede"
												}
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Fade>
								<Fade
									in={
										(
											(tipoCliente === "UPrO" &&
												usuario &&
												(idSede === 2 ? selectedLocation !== null : idSede !== -1) &&
												valorMenu?.valorMenu === 0) ||
											(tipoCliente === "Particular" &&
												idSede !== -1 &&
												(idSede === 2 ? selectedLocation !== null : true) &&
												valorMenu?.valorMenu === 0)
										) &&
										!loadingValorMenu
									}
									unmountOnExit
								>
									<SoftBox
										display="flex"
										justifyContent="center"
										alignItems="center"
										height="100%"
										minHeight="50vh"
									>
										<SoftBox
											display="flex"
											justifyContent="center"
											alignItems="center"
											flexDirection="column"
										>
											<SoftBox
												component="img"
												src={noMenuIcon}
												alt="UPrO Logo"
												width="10rem"
												p={3}
												sx={{
													animation: "shakes 1s",
													"@keyframes shakes": {
														"0%": {
															transform: "rotate(0deg)",
														},
														"25%": {
															transform: "rotate(10deg)",
														},
														"50%": {
															transform: "rotate(-20deg)",
														},
														"75%": {
															transform: "rotate(15deg)",
														},
														"100%": {
															transform: "rotate(-5deg)",
														},
													},
												}}
											/>
											<SoftTypography variant="h6" align="center">
												{tipoCliente === "UPrO"
													? "Este usuario no se encuentra habilitado para comprar en esta sede"
													: "No hay menú disponible para esta sede"
												}
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Fade>
								<Fade in={
									menusDelDia?.menusDelDia.length === 0 &&
									!loadingValorMenu &&
									(
										(tipoCliente === "UPrO" && usuario && idSede !== -1) ||
										(tipoCliente === "Particular" && idSede !== -1)
									)
								} unmountOnExit>
									<SoftBox
										display="flex"
										justifyContent="center"
										alignItems="center"
										height="100%"
										minHeight="50vh"
									>
										<SoftBox
											display="flex"
											justifyContent="center"
											alignItems="center"
											flexDirection="column"
										>
											<SoftBox
												component="img"
												src={noMenuIcon}
												alt="UPrO Logo"
												width="10rem"
												p={3}
												sx={{
													animation: "shakes 1s",
													"@keyframes shakes": {
														"0%": {
															transform: "rotate(0deg)",
														},
														"25%": {
															transform: "rotate(10deg)",
														},
														"50%": {
															transform: "rotate(-20deg)",
														},
														"75%": {
															transform: "rotate(15deg)",
														},
														"100%": {
															transform: "rotate(-5deg)",
														},
													},
												}}
											/>
											<SoftTypography variant="h6" align="center">
												No hay productos disponibles para el turno seleccionado, vuelve más tarde.
											</SoftTypography>
										</SoftBox>
									</SoftBox>
								</Fade>
								<Fade
									in={
										menusDelDia?.menusDelDia?.length > 0 &&
										(idSede === 2 ? selectedLocation !== null : idSede !== -1) &&
										!loadingValorMenu &&
										(
											(tipoCliente === "UPrO" && usuario && idSede !== -1 && valorMenu?.valorMenu > 0) ||
											(tipoCliente === "Particular" && idSede !== -1 && valorMenu?.valorMenu > 0)
										)
									}
									unmountOnExit
								>
									<SoftBox display="flex" flexDirection="column" gap={2}>
										<Grid container spacing={3} pt={1}>
											{menusDelDia?.menusDelDia.map((item) => {
												const producto = item.producto;
												return (
													<Grid item xs={12} sm={6} md={4} key={item.id}>
														<Card
															sx={{
																display: "flex",
																flexDirection: "column",
																height: "100%",
																borderRadius: 3,
																overflow: "hidden",
																border: "1px solid rgba(0, 0, 0, 0.12)",
																transition: "transform 0.3s ease-in-out",
																"&:hover": { transform: "scale(1.02)" },
															}}
														>
															<SoftBox
																sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
															>
																{producto.files && producto.files.length > 0 ? (
																	<SoftBox
																		sx={{
																			position: "relative",
																			width: "100%",
																			paddingBottom: "100%",
																			backgroundColor: "#f5f5f5",
																		}}
																	>
																		<SoftBox
																			component="img"
																			src={resolveStoredFileUrl(producto.files[0].file)}
																			alt={producto.nombre}
																			sx={{
																				position: "absolute",
																				top: 0,
																				left: 0,
																				width: "100%",
																				height: "100%",
																				objectFit: "cover",
																			}}
																		/>
																	</SoftBox>
																) : (
																	<SoftBox
																		sx={{
																			position: "relative",
																			width: "100%",
																			paddingBottom: "100%",
																			backgroundColor: "#f5f5f5",
																		}}
																	>
																		<SoftBox
																			sx={{
																				position: "absolute",
																				top: 0,
																				left: 0,
																				width: "100%",
																				height: "100%",
																				display: "flex",
																				justifyContent: "center",
																				alignItems: "center",
																			}}
																		>
																			<SoftBox
																				component="img"
																				src={
																					producto?.tipoProducto?.tipo === "Vegetariano"
																						? vegetariano
																						: producto?.tipoProducto?.tipo === "Libre de gluten"
																							? sinTacc
																							: menuIcon
																				}
																				alt={producto.nombre}
																				sx={{
																					width: "50%",
																					height: "50%",
																					objectFit: "contain",
																				}}
																			/>
																			<SoftBox
																				sx={{ position: "absolute", top: 0, right: 0 }}
																				p={2}
																			>
																				<SoftBadge
																					color="primary"
																					badgeContent={`$${formatMoneyPunto(
																						valorMenu?.valorMenu
																					)}`}
																					container
																				/>
																			</SoftBox>
																		</SoftBox>
																	</SoftBox>
																)}

																{/* Título y subtítulo */}
																<SoftBox p={2}>
																	<SoftTypography variant="h6">
																		{makeNiceText(producto.nombre)}
																	</SoftTypography>
																	{producto.tipoProducto?.tipo && (
																		<SoftTypography variant="h7" fontWeight="regular">
																			{makeNiceText(producto.tipoProducto.tipo)}
																		</SoftTypography>
																	)}
																</SoftBox>
															</SoftBox>
															<SoftBox p={2} pt={0}>
																<SoftButton
																	color={
																		selectedProducto
																			? producto.id === selectedProducto?.id
																				? "uproGreen"
																				: "secondary"
																			: "uproGreen"
																	}
																	fullWidth
																	disabled={producto.cantidadReservas >= producto.limiteReservas}
																	onClick={() => {
																		if (selectedProducto?.id === producto.id) {
																			setSelectedProducto(null);
																		} else {
																			setSelectedProducto(producto);
																		}
																	}}
																	circular
																>
																	{producto.cantidadReservas >= producto.limiteReservas
																		? "Agotado"
																		: selectedProducto?.id === producto.id
																			? "Seleccionado"
																			: "Seleccionar"}
																	&nbsp;
																	<Icon>
																		{selectedProducto?.id === producto.id
																			? "done"
																			: "arrow_forward"}
																	</Icon>
																</SoftButton>
															</SoftBox>
														</Card>
													</Grid>
												);
											})}
										</Grid>
									</SoftBox>
								</Fade>
								<SoftBox
									display={{
										xs: "flex-row",
										sm: "flex",
									}}
									justifyContent="flex-end"
									alignItems="center"
									mt={2}
								>
									<SoftBox
										display={{
											xs: "flex-row",
											sm: "flex",
										}}
										justifyContent="end"
										alignItems="center"
									>
										<SoftBox
											mr={{
												xs: 0,
												sm: 2,
											}}
											mt={{
												xs: 2,
												sm: 0,
											}}
										>
											<SoftButton
												color="primary"
												circular
												fullWidth={{
													xs: true,
													sm: false,
												}}
												onClick={() => {
													if (!loadingSaveVenta) {
														handleClose();
													}
												}}
											>
												<Icon sx={{ fontWeight: "light" }}>cancel</Icon>
												&nbsp;Cancelar
											</SoftButton>
										</SoftBox>
										<SoftBox
											mt={{
												xs: 2,
												sm: 0,
											}}
										>
											<SoftButton
												color="uproGreen"
												circular
												disabled={
													(!usuario && tipoCliente !== "Particular") ||
													!selectedProducto ||
													!idSede ||
													(idSede === 2 && !selectedLocation) ||
													loadingSaveVenta ||
													loadingValorMenu
												}
												onClick={() => {
													venderMenuUniversitario({
														variables: {
															idProducto: selectedProducto?.id,
															idUsuario: usuario?.id,
															appUsuario: usuario?.appUsuario,
															nombre: usuario?.nombre,
															apellido: usuario?.apellido,
															nro_doc: usuario?.dni,
															tipoUsuario: usuario?.tipoUser || usuario?.tipoUsuario,
															total: valorMenu?.valorMenu,
															idSede: idSede,
															destino: selectedLocation,
														},
													})
														.then((res) => {
															if (res.data.venderMenuUniversitario) {
																if (res.data.venderMenuUniversitario?.url) {
																	handleSnackbar("Pedido generado correctamente", "success");
																} else {
																	handleSnackbar(
																		"Se realizó el pedido pero hubo un error al facturar en ARCA, intenta facturar nuevamente más tarde",
																		"warning"
																	);
																}
																handleClose();
															}
														})
														.catch((err) => {
															handleSnackbar(
																err.message || "Error al generar el pedido",
																"error"
															);
														});
												}}
												fullWidth={{
													xs: true,
													sm: false,
												}}
											>
												{loadingSaveVenta ? "Facturando..." : "Finalizar"}&nbsp;
												{loadingSaveVenta ? (
													<CircularProgress size={15} color="inherit" />
												) : (
													<Icon>done_all</Icon>
												)}
											</SoftButton>
										</SoftBox>
									</SoftBox>
								</SoftBox>
							</SoftBox>
						</SoftBox>
					</SoftBox>
				</Card>
			</Fade>
		</Modal>
	);
}

ModalNuevoPedido.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
};
